import { useMemo, useState } from "react";
import DashboardCard from "../components/card/DashboardCard";
import { Calendar, CircleAlert, Clock, LogOut, Users } from "lucide-react";
import TotalVisitorsCard from "../components/card/TotalVisitorsCard";
import VisitorsByTypeCard from "../components/card/VisitorsByTypeCard";
import useAuthStore from "../store/Auth";
import { USER_ROLES_IDS } from "../constants/common";
import CustomTabs from "../components/tabs";
import { useGetVisitorsCount } from "../hooks/dashboard/query";
import VisitorsByNationalityCard from "../components/card/VisitorsByNationalityCard";
import CurrentlyOverStayedVisitorsTable from "../components/table/CurrentlyOverStayedVisitorsTable";
import { useTranslation } from "react-i18next";
import CheckedInVisitorsTable from "../components/table/CheckedInVisitorsTable";
import CheckedOutVisitorsTable from "../components/table/CheckedOutVisitorsTable";
import PendingVisitorsTable from "../components/table/PendingVisitorsTable";
import ExpectedVisitorsTable from "../components/table/ExpectedVisitorsTable";

const Home = () => {
  const user = useAuthStore((state) => state.user);

  const [tabIndex, setTabIndex] = useState(
    user?.n_RoleID === USER_ROLES_IDS.RECEPTION
      ? 4
      : user?.n_RoleID === USER_ROLES_IDS.SECURITY ||
        user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER
      ? 3
      : 0
  );

  const [selectedVisitDurationType, setSelectedVisitDurationType] = useState(0);
  const [selectedPendingApprovalRole, setSelectedPendingApprovalRole] =
    useState(0);

  const { t } = useTranslation();

  const { data: visitorsCount, refetch: refetchVisitorsCount } =
    useGetVisitorsCount({
      encryptedLocationID:
        user?.n_RoleID !== USER_ROLES_IDS.ADMIN
          ? user?.n_LocationID?.toString()
            ? user?.n_LocationID?.toString()
            : ""
          : "",
      filterRoleID: selectedPendingApprovalRole || 0,
    });

  const visitorTables = [
    <CheckedInVisitorsTable />,
    <CheckedOutVisitorsTable />,
    <CurrentlyOverStayedVisitorsTable />,
    <PendingVisitorsTable
      setSelectedPendingApprovalRole={setSelectedPendingApprovalRole}
    />,
    <ExpectedVisitorsTable visitDurationType={selectedVisitDurationType} />,
  ];

  const dashboardCardsData = useMemo(
    () => [
      {
        id: 1,
        key: 1,
        title: t("checkedInVisitors"),
        count: visitorsCount?.currentVisitorsOnSite,
        icon: <Users className="w-5" />,
        notification: visitorsCount?.currentVisitorsOnSiteRiseOrFallPercentage,
      },
      {
        id: 2,
        key: 2,
        title: t("checkedOutVisitors"),
        count: visitorsCount?.checkedOutVisitors,
        icon: <LogOut className="w-5" />,
        notification: visitorsCount?.checkedOutVisitorsRiseOrFallPercentage,
      },
      {
        id: 3,
        key: 3,
        title: t("overstayedVisitors"),
        count: visitorsCount?.overStayedVisitors,
        icon: <Clock className="w-5" />,
        notification: visitorsCount?.overStayedVisitorsRiseOrFallPercentage,
      },
      {
        id: 4,
        key: 4,
        title: t("pendingApprovals"),
        count: visitorsCount?.pendingApprovals,
        icon: <CircleAlert className="w-5" />,
        notification: visitorsCount?.pendingApprovalsRiseOrFallPercentage,
      },
      {
        id: 5,
        key: 5,
        title: t("todaysExpectedVisitorsOneDay"),
        count: visitorsCount?.todaysExpectedVisitorsOneDay,
        icon: <Calendar className="w-5" />,
        notification:
          visitorsCount?.todaysExpectedVisitorsRiseOrFallPercentageOneDay,
      },
      {
        id: 6,
        key: 5,
        title: t("todaysExpectedVisitorsMultipleDays"),
        count: visitorsCount?.todaysExpectedVisitorsMultipleDays,
        icon: <Calendar className="w-5" />,
        notification:
          visitorsCount?.todaysExpectedVisitorsRiseOrFallPercentageMultipleDays,
      },
    ],
    [t, visitorsCount]
  );

  const dashboardTabs = useMemo(
    () => [
      { id: 1, title: t("checkedInVisitors") },
      { id: 2, title: t("checkedOutVisitors") },
      { id: 4, title: t("overstayedVisitors") },
      { id: 3, title: t("pendingApprovals") },
      { id: 5, title: t("todaysExpectedVisitors") },
    ],
    [t]
  );

  return (
    <div className="flex flex-col gap-4 md:gap-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {dashboardCardsData.map((item) => (
          <DashboardCard
            key={item.id}
            data={item}
            setTabIndex={setTabIndex}
            refetch={refetchVisitorsCount}
            setSelectedVisitDurationType={setSelectedVisitDurationType}
          />
        ))}
      </div>

      {user?.n_RoleID === USER_ROLES_IDS.ADMIN && (
        <div className="flex flex-col xl:flex-row lg:items-center gap-6 md:gap-4">
          <TotalVisitorsCard />
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-4 w-full xl:w-[50%]">
            <VisitorsByTypeCard />
            <VisitorsByNationalityCard />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-2">
          <CustomTabs
            tabs={dashboardTabs}
            tabIndex={tabIndex}
            setTabIndex={setTabIndex}
            refetch={refetchVisitorsCount}
          />
        </div>

        {visitorTables[tabIndex]}
      </div>
    </div>
  );
};

export default Home;
